import type { PageSection, PageSectionWithLocale } from '@/hooks/useLpg';
import { type Config as CarTemplateConfig, carPpmTemplateConfig, carTemplateConfig } from '@/pageConfig/car';
import { type Config as HomeTemplateConfig, homeTemplateConfig } from '@/pageConfig/home';
import { type Config as HomeownersTemplateConfig, homeownersTemplateConfig } from '@/pageConfig/homeowners';
import { type Config as LifeTemplateConfig, lifeTemplateConfig } from '@/pageConfig/life';
import { type Config as PetTemplateConfig, petTemplateConfig } from '@/pageConfig/pet/pet';
import { type Config as RentersTemplateConfig, rentersTemplateConfig } from '@/pageConfig/renters';
import type { Locale } from '@lemonade-hq/lemonation';
import type { ProductsByHeroSection, StaticComponentProps } from './lpgTypesAndDefinitions';
import type { SectionsType } from './lpgTypesAndDefinitions';

// extend this type with more products config types
type TemplatesConfigByLocale =
  | Record<Locale, HomeTemplateConfig>
  | Record<Locale, RentersTemplateConfig>
  | Record<Locale, PetTemplateConfig>
  | Record<Locale, HomeownersTemplateConfig>
  | Record<Locale, LifeTemplateConfig>
  | Record<Locale, CarTemplateConfig>;

const CONFIG_BY_PRODUCT_TEMPLATE = {
  default: homeTemplateConfig,
  renters: rentersTemplateConfig,
  pet: petTemplateConfig,
  home: homeownersTemplateConfig,
  life: lifeTemplateConfig,
  car: carTemplateConfig,
  // Extend LPG config: add more product and config maps by locale here
} as Record<ProductsByHeroSection | 'default', TemplatesConfigByLocale>;

const productPageSections = {
  hero: ['renters_hero', 'pet_hero', 'ho_hero', 'life_hero', 'car_hero'],
  reviews: ['reviews', 'renters_reviews', 'ho_reviews', 'car_reviews'],
  product: ['products', 'renters_products', 'ho_products'],
} as Record<string, SectionsType[]>;

export const getExtraPropsBySectionType = ({
  type,
  props,
  locale,
  product,
  isCarPpm,
}: PageSectionWithLocale & {
  product: ProductsByHeroSection | undefined;
  isCarPpm: boolean;
}): StaticComponentProps & Pick<PageSection, 'props'> => {
  // this is only need it for those sections with props in their config that are not present in from lpg data
  const configByLocale = isCarPpm
    ? carPpmTemplateConfig[locale]
    : CONFIG_BY_PRODUCT_TEMPLATE[product ?? 'default'][locale];

  const isCustomerReviews = productPageSections.reviews.includes(type);
  const isProducts = productPageSections.product.includes(type);
  const isProductPageHero = productPageSections.hero.includes(type);

  if (isCustomerReviews && 'customersReviews' in configByLocale)
    return {
      ...configByLocale.customersReviews,
      ...props,
    };

  if (isProducts && 'products' in configByLocale)
    return {
      ...configByLocale.products,
      ...props,
    };

  if (type === 'instant' && 'instantEverything' in configByLocale)
    return {
      ...configByLocale.instantEverything,
      ...props,
    };

  if (type === 'switching' && 'switching' in configByLocale)
    return {
      ...configByLocale.switching,
      ...props,
    };

  if (type === 'how_it_works' && 'howItWorks' in configByLocale)
    return {
      ...configByLocale.howItWorks,
      ...props,
    };

  if (type === 'trust' && 'trust' in configByLocale)
    return {
      ...configByLocale.trust,
      ...props,
    };

  if (type === 'eu_products' && 'genericProducts' in configByLocale)
    return {
      ...configByLocale.genericProducts,
      ...props,
    };

  if (type === 'eu_switching' && 'switchingEurope' in configByLocale)
    return {
      ...configByLocale.switchingEurope,
      ...props,
    };

  if (type === 'eu_switching' && 'superCharge' in configByLocale)
    return {
      ...configByLocale.superCharge,
      ...props,
    };

  if (type === 'pricing' && 'prices' in configByLocale)
    return {
      ...configByLocale.prices,
      ...props,
    };

  // product pages hero section
  if (isProductPageHero && 'hero' in configByLocale) {
    return {
      ...configByLocale.hero,
      ...props,
    };
  }

  if (type === 'ho_giving_back' && 'bottomLogos' in configByLocale) {
    return {
      ...configByLocale.bottomLogos,
      ...props,
    };
  }

  if (type === 'ho_faqs' && 'faq' in configByLocale) {
    return {
      darkBackground: true,
      ...configByLocale.faq,
      ...props,
    };
  }

  if (type === 'life_reviews' && 'trusted' in configByLocale) {
    return {
      ...configByLocale.trusted,
      ...props,
    };
  }

  if (type === 'life_flow_steps' && 'coverages' in configByLocale) {
    return {
      ...configByLocale.coverages,
      ...props,
    };
  }

  if (type === 'car_overall_coverages' && 'overallCoverages' in configByLocale) {
    return {
      ...configByLocale.overallCoverages,
      ...props,
    };
  }

  if (type === 'ho_coverage' && 'coverage' in configByLocale) {
    return {
      ...configByLocale.coverage,
      ...props,
    };
  }

  if (type === 'car_basic_coverages' && 'basicCoverages' in configByLocale) {
    return {
      ...configByLocale.basicCoverages,
      ...props,
    };
  }

  if (type === 'car_faq' && 'faq' in configByLocale) {
    return {
      ...configByLocale.faq,
      ...props,
    };
  }

  if (type === 'car_low_mileage' && 'lowMilage' in configByLocale) {
    return {
      ...configByLocale.lowMilage,
      ...props,
    };
  }

  if (type === 'car_low_mileage_ppm' && 'lowMilagePpm' in configByLocale) {
    return {
      ...configByLocale.lowMilagePpm,
      ...props,
    };
  }

  if (type === 'car_electric_coverages' && 'electricCoverages' in configByLocale) {
    return {
      ...configByLocale.electricCoverages,
      ...props,
    };
  }

  // Extend LPG props: add more props based on type

  // Important! Return hero (home page) section config with props by default
  return {
    ...configByLocale.hero,
    ...props,
  };
};
